import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { gapi, errMsg, fmtDateTime, fmtEuro } from "../api";
import { useGAuth } from "../GAuth";
import { STATI_PUBBLICAZIONE, PUB_TONE } from "../labels";
import { PageHeader, Panel, Btn, Badge, inputCls } from "../components/ui";
import Timeline from "../components/Timeline";
import MatchList from "../components/MatchList";
import { Generale, Posizione, Caratteristiche, Prezzo, Proprietari, Testi, Media } from "./ImmobileSections";
import { fileUrl } from "@/lib/api";
import { DocChecklist } from "../components/Documenti";

const NEW = { titolo: "", transazione: "vendita", destinazione: "residenziale", statoCommerciale: "disponibile", stato: "bozza", posizionePubblica: "approssimativa", foto: [], planimetrie: [], video: [], proprietari: [], puntiForza: [], riservato: {} };

function Anteprima({ a }) {
  if (!a) return null;
  return (
    <Panel title="Anteprima annuncio pubblico" testid="prop-public-preview">
      {a.immagini?.[0] && <img src={fileUrl(a.immagini[0])} alt="" className="h-40 w-full object-cover rounded-xl" />}
      <p className="mt-3 font-display text-2xl font-semibold">{a.titolo}</p>
      <p className="text-sm text-anthracite/60">{a.comune} · {a.zona}{a.indirizzo ? ` · ${a.indirizzo}` : ""}</p>
      <p className="mt-1 font-semibold text-brand">{fmtEuro(a.prezzo)}</p>
      <p className="text-xs text-anthracite/50 mt-2">{a.superficie || "—"} mq · {a.locali || "—"} locali · {a.bagni || "—"} bagni · classe {a.classeEnergetica || "n.d."}</p>
      <p className="text-sm mt-2 line-clamp-4">{a.descrizione || "Descrizione non inserita."}</p>
      <p className="text-[11px] text-anthracite/45 mt-3">Il sito riceve solo questi dati: niente proprietari, documenti, note interne, prezzo minimo o dati catastali.</p>
    </Panel>
  );
}

function Chiavi({ p, reload }) {
  const [f, setF] = useState({ codice: "", detentore: "", note: "" });
  const add = async (e) => {
    e.preventDefault();
    try { await gapi.post(`/properties/${p.id}/keys`, f); setF({ codice: "", detentore: "", note: "" }); reload(); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const ret = async (k) => { try { await gapi.post(`/keys/${k.id}/return`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel title="Registro chiavi" testid="prop-keys">
      <ul className="text-sm space-y-2 mb-3">
        {(p.chiavi || []).map((k) => (
          <li key={k.id} className="flex justify-between gap-2"><span><b>{k.codice}</b> · {k.detentore}<br /><span className="text-xs text-anthracite/50">Consegnate {fmtDateTime(k.consegnataIl)}{k.restituitaIl ? ` · restituite ${fmtDateTime(k.restituitaIl)}` : ""}</span></span>
            {!k.restituitaIl ? <Btn variant="ghost" className="h-8 text-xs" onClick={() => ret(k)} data-testid="key-return">Restituite</Btn> : <Badge tone="green">rientrate</Badge>}</li>
        ))}
      </ul>
      <form onSubmit={add} className="grid grid-cols-2 gap-2">
        <input required className={inputCls} placeholder="Codice chiave" value={f.codice} onChange={(e) => setF({ ...f, codice: e.target.value })} data-testid="key-code" />
        <input required className={inputCls} placeholder="Consegnate a" value={f.detentore} onChange={(e) => setF({ ...f, detentore: e.target.value })} data-testid="key-holder" />
        <Btn type="submit" className="col-span-2" data-testid="key-add">Registra consegna</Btn>
      </form>
    </Panel>
  );
}

export default function ImmobileEditor() {
  const { id } = useParams();
  const isNew = id === "nuovo";
  const navigate = useNavigate();
  const { can, user } = useGAuth();
  const [p, setP] = useState(isNew ? { ...NEW, responsabileId: user.id } : null);
  const [users, setUsers] = useState([]);
  const [matches, setMatches] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (isNew) return;
    gapi.get(`/properties/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(errMsg(e)));
    gapi.get(`/properties/${id}/timeline`).then((r) => setTimeline(r.data)).catch(() => {});
    if (can("richieste")) gapi.get(`/properties/${id}/matches`).then((r) => setMatches(r.data)).catch(() => setMatches([]));
  }, [id, isNew]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); gapi.get("/users/options").then((r) => setUsers(r.data)).catch(() => {}); }, [load]);

  if (!p) return <p className="text-sm text-anthracite/50">Caricamento…</p>;
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });
  const editable = isNew ? can("immobili", "creare") : can("immobili", "modificare");

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...p };
      ["anteprimaPubblica", "chiavi", "proprietariDettaglio", "prezzoStorico", "riservatoNascosto"].forEach((k) => delete body[k]);
      if (p.riservatoNascosto) delete body.riservato;
      const { data } = isNew ? await gapi.post("/properties", body) : await gapi.put(`/properties/${id}`, body);
      toast.success(`Salvato${data.nuoviAbbinamenti ? ` · ${data.nuoviAbbinamenti} nuovi abbinamenti` : ""}${data.stato === "da_approvare" && p.stato === "pubblicato" ? " · inviato in approvazione" : ""}`);
      if (isNew) navigate(`/gestionale/immobili/${data.id}`, { replace: true }); else load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const act = async (path, ok) => { try { await gapi.post(path); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="property-editor">
      <PageHeader eyebrow={<Link to="/gestionale/immobili">← Immobili</Link>} title={isNew ? "Nuovo immobile" : p.titolo}
        desc={!isNew && <span className="flex flex-wrap gap-2 items-center">{p.codiceRif} · aggiornato {fmtDateTime(p.updatedAt)} <Badge tone={PUB_TONE[p.stato]}>{STATI_PUBBLICAZIONE[p.stato]}</Badge></span>}
        actions={<>
          {!isNew && p.stato === "pubblicato" && <a href={`/immobili/${id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-brand font-semibold px-2" data-testid="prop-open-public">Vedi sul sito <ExternalLink className="h-4 w-4" /></a>}
          {!isNew && <Link to={`/gestionale/assistente-ai?propertyId=${id}`} className="inline-flex items-center gap-2 rounded-full px-4 h-10 text-sm font-semibold border border-border-subtle bg-white hover:bg-accent-subtle" data-testid="prop-ai-draft">Bozza descrizione AI</Link>}
          {!isNew && <Btn variant="ghost" onClick={() => gapi.get(`/properties/${id}/report-proprietario`, { responseType: "blob" }).then((r) => { const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = `report-${p.codiceRif}.pdf`; a.click(); }).catch(() => toast.error("Report non disponibile"))} data-testid="prop-owner-report">Report proprietario PDF</Btn>}
          {!isNew && can("immobili", "archiviare") && (p.stato === "archiviato"
            ? <Btn variant="ghost" onClick={() => act(`/properties/${id}/restore`, "Ripristinato")} data-testid="prop-restore">Ripristina</Btn>
            : <Btn variant="ghost" onClick={() => act(`/properties/${id}/archive`, "Archiviato e rimosso dal sito")} data-testid="prop-archive">Archivia</Btn>)}
          {editable && <Btn onClick={save} disabled={saving} data-testid="prop-save">{saving ? "Salvataggio…" : "Salva"}</Btn>}
        </>} />
      <div className="grid lg:grid-cols-3 gap-5">
        <fieldset disabled={!editable} className="lg:col-span-2 space-y-5 min-w-0">
          <Generale p={p} set={set} setP={setP} users={users} canPublish={can("immobili", "pubblicare")} wasPublished={p.stato === "pubblicato"} />
          <Posizione p={p} set={set} setP={setP} />
          <Caratteristiche p={p} set={set} setP={setP} />
          <Prezzo p={p} set={set} setP={setP} canReserved={can("dati_riservati_immobili", "modificare")} />
          {can("contatti") && <Proprietari p={p} setP={setP} />}
          <Testi p={p} set={set} setP={setP} />
          <Media p={p} setP={setP} />
        </fieldset>
        <div className="space-y-5 min-w-0">
          <Anteprima a={p.anteprimaPubblica} />
          {!isNew && can("richieste") && <Panel title="Acquirenti compatibili" testid="prop-matches" actions={<Btn variant="ghost" className="h-9" onClick={() => gapi.get(`/properties/${id}/matches`, { params: { ricalcola: "1" } }).then((r) => { setMatches(r.data); toast.success(`${r.data.length} acquirenti trovati`); })} data-testid="prop-find-buyers">Trova acquirenti compatibili</Btn>}><MatchList items={matches} mode="clienti" onChange={load} /></Panel>}
          {!isNew && <DocChecklist entita="immobile" entitaId={id} modello={p.transazione === "affitto" ? "locazione" : "vendita"} />}
          {!isNew && editable && <Chiavi p={p} reload={load} />}
          {!isNew && <Panel title="Cronologia"><Timeline items={timeline} testid="prop-timeline" /></Panel>}
        </div>
      </div>
    </div>
  );
}
